import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api";
import { useI18n } from "../i18n";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const FALLBACK_COVER = "https://images.pexels.com/photos/36373012/pexels-photo-36373012.jpeg";

export default function InsightDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [insight, setInsight] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/insights/${slug}`)
      .then((r) => setInsight(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <main className="container-x py-32">
        <div className="serif-display" style={{ fontSize: 40 }}>Insight non trovato</div>
        <Link to="/#insights" className="btn-primary mt-8 inline-flex">Torna a Insights</Link>
      </main>
    );
  }

  if (!insight) {
    return (
      <main className="container-x py-32">
        <div className="font-mono text-xs" style={{ opacity: 0.6 }}>Loading…</div>
      </main>
    );
  }

  const isCaseStudy = insight.category === "case-study";
  const backTo = isCaseStudy ? "/case-studies" : "/#insights";
  const backLabel = lang === "it"
    ? (isCaseStudy ? "Torna alle storie" : "Torna a Insights")
    : (isCaseStudy ? "Back to stories" : "Back to Insights");
  const title = lang === "it" ? insight.title_it : insight.title_en;
  const body = lang === "it" ? insight.body_it : insight.body_en;
  const excerpt = lang === "it" ? insight.excerpt_it : insight.excerpt_en;

  return (
    <main data-testid="insight-detail">
      <SEO
        title={title.replace(/[«»""]/g, "")}
        description={excerpt}
        path={`/insights/${slug}`}
        type="article"
        image={insight.cover_url && insight.cover_url.startsWith("http") ? insight.cover_url : undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": isCaseStudy ? "Article" : "Article",
          "headline": title,
          "description": excerpt,
          "image": insight.cover_url,
          "datePublished": insight.published_at,
          "author": {"@type": "Organization", "name": "GIANO", "url": "https://gianoholding.com/"},
          "publisher": {"@id": "https://gianoholding.com/#organization"},
          "mainEntityOfPage": `https://gianoholding.com/insights/${slug}`,
          "inLanguage": lang === "it" ? "it-IT" : "en-US",
          "articleSection": isCaseStudy ? "Case Study" : "Insight"
        }}
      />
      {/* HERO */}
      <section style={{ background: "var(--card)" }} className="py-16 md:py-24">
        <div className="container-x">
          <Link to={backTo} className="inline-flex items-center gap-2 font-mono text-xs mb-8" style={{ opacity: 0.7 }} data-testid="back-to-insights">
            <ArrowLeft size={14} /> {backLabel}
          </Link>
          <div className="overline" style={{ color: "var(--terracotta)" }}>{isCaseStudy ? (lang === "it" ? "STORIA" : "STORY") : insight.category.toUpperCase()}</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 72px)", maxWidth: 980 }}>
            {title}
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 720, opacity: 0.78, lineHeight: 1.7 }}>
            {excerpt}
          </p>
          <div className="mt-8 font-mono text-xs" style={{ opacity: 0.6 }}>
            {new Date(insight.published_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
            {" · "}Team M&amp;A GIANO
          </div>
        </div>
      </section>

      {/* COVER */}
      {(insight.cover_url || FALLBACK_COVER) && (
        <section>
          <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 1.5rem" }}>
            {(() => {
              const cover = insight.cover_url || FALLBACK_COVER;
              const isSvg = cover.endsWith(".svg");
              return (
                <div style={{
                  aspectRatio: isSvg ? "16/6" : "16/7",
                  overflow: "hidden",
                  border: "1px solid var(--border)",
                  background: isSvg ? "var(--bone)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <img src={cover} alt={title}
                       style={{
                         width: isSvg ? "60%" : "100%",
                         height: isSvg ? "80%" : "100%",
                         objectFit: isSvg ? "contain" : "cover",
                         filter: "saturate(0.9)",
                       }} />
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* BODY */}
      <section className="py-16 md:py-24">
        <div className="container-x">
          <article className="mx-auto" style={{ maxWidth: 720 }}>
            {body.split("\n\n").map((para, i) => {
              const trimmed = para.trim();
              // Per le case-study (monologhi in prima persona), il primo paragrafo
              // è l'attribuzione "Scenario illustrativo…" — stile eyebrow italico.
              if (isCaseStudy && i === 0) {
                return (
                  <p key={i} className="mb-10 italic" style={{
                    fontSize: 15,
                    lineHeight: 1.65,
                    color: "var(--terracotta)",
                    paddingBottom: 20,
                    borderBottom: "1px solid var(--border)",
                  }}>
                    {trimmed}
                  </p>
                );
              }
              // Fallback per gli insight strutturati con heading ALL-CAPS
              const isHeading = trimmed.length < 60 && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed) && !trimmed.includes(".");
              if (isHeading) {
                return (
                  <h3 key={i} className="font-mono mt-12 mb-6" style={{
                    fontSize: 13,
                    letterSpacing: "0.14em",
                    color: "var(--terracotta)",
                  }}>
                    {trimmed}
                  </h3>
                );
              }
              return (
                <p key={i} className="mb-6 text-lg" style={{ lineHeight: 1.75, color: "#0F1720" }}>
                  {para}
                </p>
              );
            })}
          </article>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-20">
        <div className="container-x text-center">
          <div className="overline" style={{ opacity: 0.7 }}>Vuoi parlare con noi?</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(32px, 4vw, 48px)", maxWidth: 720, margin: "0 auto" }}>
            Se stai valutando il futuro della tua azienda, il primo passo è una conversazione riservata.
          </h2>
          <Link to="/#contact" className="btn-primary mt-10 inline-flex" style={{ background: "var(--bone)", color: "var(--midnight)", borderColor: "var(--bone)" }}>
            Contattaci
          </Link>
        </div>
      </section>
    </main>
  );
}
